import type {
  OutcomeCopy,
  OutcomeKey,
  PetLossLocale,
  ReadinessQuestion
} from './types';

export const DEFAULT_PET_LOSS_QUESTIONS: ReadinessQuestion[] = [
  {
    id: 'recency',
    prompt: 'How recently did you lose your pet?',
    options: [
      {
        value: 'weeks',
        label: 'Within the past few weeks',
        soon: 2,
        foster: 0,
        ready: 0
      },
      {
        value: '1-6-months',
        label: '1–6 months ago',
        soon: 1,
        foster: 0,
        ready: 0
      },
      {
        value: '6-12-months',
        label: '6–12 months ago',
        soon: 0,
        foster: 1,
        ready: 1
      },
      {
        value: 'over-year',
        label: 'More than a year ago',
        soon: 0,
        foster: 0,
        ready: 2
      }
    ]
  },
  {
    id: 'feeling',
    prompt: 'When you think of your pet right now, what comes up most?',
    options: [
      {
        value: 'raw-grief',
        label: 'Mostly raw grief — it still hurts a lot',
        soon: 2,
        foster: 0,
        ready: 0
      },
      {
        value: 'mixed',
        label: 'A mix of sadness and warm memories',
        soon: 1,
        foster: 1,
        ready: 1
      },
      {
        value: 'fond',
        label: 'Mostly fond memories and gratitude',
        soon: 0,
        foster: 0,
        ready: 2
      },
      {
        value: 'at-peace',
        label: 'I feel at peace and ready to love again',
        soon: 0,
        foster: 0,
        ready: 2
      }
    ]
  },
  {
    id: 'motivation',
    prompt: "What's drawing you toward another pet now?",
    options: [
      {
        value: 'fill-emptiness',
        label: 'To fill the emptiness as soon as possible',
        soon: 2,
        foster: 1,
        ready: 0
      },
      {
        value: 'family-asking',
        label: 'My family or kids want one more than I do',
        soon: 1,
        foster: 1,
        ready: 0
      },
      {
        value: 'companionship',
        label: 'I miss the companionship and routine',
        soon: 0,
        foster: 1,
        ready: 1
      },
      {
        value: 'reflected',
        label: "I've reflected and feel genuinely ready",
        soon: 0,
        foster: 0,
        ready: 2
      }
    ]
  },
  {
    id: 'household',
    prompt: 'How is everyone in your home doing with the loss?',
    options: [
      {
        value: 'just-me',
        label: "It's just me — no one else to consider",
        soon: 0,
        foster: 0,
        ready: 0
      },
      {
        value: 'grieving-deeply',
        label: "We're all still grieving deeply",
        soon: 2,
        foster: 0,
        ready: 0
      },
      {
        value: 'split',
        label: "Some are ready, some aren't",
        soon: 1,
        foster: 1,
        ready: 0
      },
      {
        value: 'time-to-give',
        label: 'Feeling healed with time and attention to give',
        soon: 0,
        foster: 0,
        ready: 2
      }
    ]
  },
  {
    id: 'openness',
    prompt: 'Have you considered adoption or fostering as a next step?',
    options: [
      {
        value: 'not-yet',
        label: "Haven't thought about it",
        soon: 1,
        foster: 2,
        ready: 0
      },
      {
        value: 'open',
        label: 'Open to it — tell me more',
        soon: 0,
        foster: 2,
        ready: 1
      },
      {
        value: 'foster-first',
        label: 'Fostering first feels right for me',
        soon: 0,
        foster: 2,
        ready: 0
      },
      {
        value: 'know-what-i-want',
        label: "I've weighed my options and know what I want",
        soon: 0,
        foster: 0,
        ready: 2
      }
    ]
  }
];

export const DEFAULT_OUTCOME_COPY: Record<OutcomeKey, OutcomeCopy> = {
  soon: {
    badge: 'Be gentle with yourself',
    heading: "It may be too soon — and that's okay",
    body: 'Your answers suggest you\'re still in the thick of grief. There\'s no rush and no "right" timeline. Rather than make a big decision right now, the kindest next step may be to lean on support and give yourself room to heal. A new companion will still be there when the time is right.'
  },
  foster: {
    badge: 'A middle path',
    heading: 'Consider adoption or fostering first',
    body: "You may be ready to open your heart again — and there's a gentle way to test that. Fostering or adopting lets you give a home to a pet in need without the full commitment of a puppy right away, and many people find it a healing bridge after loss."
  },
  ready: {
    badge: "You've done the reflection",
    heading: "You may be ready — here's how to move forward responsibly",
    body: "It sounds like you've grieved, reflected, and feel genuinely ready to welcome a new companion. The most loving thing you can do now is choose responsibly — understanding the real commitment and working only with ethical, verified breeders or rescues. These free resources will help."
  }
};

const FRENCH_QUESTION_TEXT: Record<
  string,
  { prompt: string; options: Record<string, string> }
> = {
  recency: {
    prompt: 'Depuis combien de temps avez-vous perdu votre animal ?',
    options: {
      weeks: 'Au cours des dernières semaines',
      '1-6-months': 'Il y a 1 à 6 mois',
      '6-12-months': 'Il y a 6 à 12 mois',
      'over-year': "Il y a plus d'un an"
    }
  },
  feeling: {
    prompt:
      "Quand vous pensez à votre animal en ce moment, qu'est-ce qui ressort le plus ?",
    options: {
      'raw-grief': 'Surtout un chagrin vif — ça fait encore très mal',
      mixed: 'Un mélange de tristesse et de doux souvenirs',
      fond: 'Surtout de beaux souvenirs et de la gratitude',
      'at-peace': 'Je me sens en paix et prêt(e) à aimer de nouveau'
    }
  },
  motivation: {
    prompt: "Qu'est-ce qui vous attire vers un nouvel animal maintenant ?",
    options: {
      'fill-emptiness': 'Combler le vide le plus vite possible',
      'family-asking': 'Ma famille ou mes enfants en veulent un plus que moi',
      companionship: 'La compagnie et la routine me manquent',
      reflected: "J'y ai réfléchi et je me sens vraiment prêt(e)"
    }
  },
  household: {
    prompt: 'Comment les membres de votre foyer vivent-ils cette perte ?',
    options: {
      'just-me': "Je vis seul(e) — personne d'autre à considérer",
      'grieving-deeply': 'Nous sommes tous encore profondément en deuil',
      split: "Certains sont prêts, d'autres non",
      'time-to-give':
        "Nous nous sentons apaisés, avec du temps et de l'attention à offrir"
    }
  },
  openness: {
    prompt:
      "Avez-vous envisagé l'adoption ou l'accueil temporaire comme prochaine étape ?",
    options: {
      'not-yet': "Je n'y ai pas pensé",
      open: "J'y suis ouvert(e) — dites-m'en plus",
      'foster-first': "Être famille d'accueil d'abord me semble juste",
      'know-what-i-want': "J'ai pesé mes options et je sais ce que je veux"
    }
  }
};

export const DEFAULT_PET_LOSS_QUESTIONS_FR: ReadinessQuestion[] =
  DEFAULT_PET_LOSS_QUESTIONS.map((question) => {
    const french = FRENCH_QUESTION_TEXT[question.id];
    return {
      ...question,
      prompt: french?.prompt ?? question.prompt,
      options: question.options.map((option) => ({
        ...option,
        label: french?.options[option.value] ?? option.label
      }))
    };
  });

export const DEFAULT_OUTCOME_COPY_FR: Record<OutcomeKey, OutcomeCopy> = {
  soon: {
    badge: 'Soyez doux avec vous-même',
    heading: "C'est peut-être trop tôt — et c'est correct",
    body: "Vos réponses suggèrent que vous êtes encore au cœur du deuil. Rien ne presse et il n'y a pas de « bon » moment. Plutôt que de prendre une grande décision maintenant, la meilleure prochaine étape est peut-être de vous appuyer sur du soutien et de vous donner le temps de guérir. Un nouveau compagnon sera encore là quand le moment sera venu."
  },
  foster: {
    badge: 'Une voie intermédiaire',
    heading: "Envisagez d'abord l'adoption ou l'accueil temporaire",
    body: "Vous êtes peut-être prêt(e) à ouvrir de nouveau votre cœur — et il existe une façon douce de le vérifier. Devenir famille d'accueil ou adopter vous permet d'offrir un foyer à un animal dans le besoin sans l'engagement complet d'un chiot tout de suite, et bien des gens y trouvent un pont apaisant après une perte."
  },
  ready: {
    badge: 'Vous avez pris le temps de réfléchir',
    heading:
      'Vous êtes peut-être prêt(e) — voici comment avancer de façon responsable',
    body: "Il semble que vous ayez vécu votre deuil, réfléchi et que vous vous sentiez vraiment prêt(e) à accueillir un nouveau compagnon. Le geste le plus aimant maintenant est de choisir de façon responsable — en comprenant l'engagement réel et en faisant affaire uniquement avec des éleveurs éthiques et vérifiés ou des refuges. Ces ressources gratuites vous aideront."
  }
};

export const defaultQuestionsFor = (
  locale: PetLossLocale
): ReadinessQuestion[] =>
  locale === 'fr' ? DEFAULT_PET_LOSS_QUESTIONS_FR : DEFAULT_PET_LOSS_QUESTIONS;
